/* DIS/PLAY Script
 Author's name: Lars Munkholm <lar@dis-play.dk>
 Modified by:
 Client name:
 Date of creation: 17-02-2017


 EXAMPLES:
 =========

 Scroll window to 100px down on the Y-axis (1000ms duration)
 scrollTo(100);

 Scroll window to 1024px on the X-axis and 100px on the Y-axis (500ms duration)
 scrollTo([1024, 100], 500);

 Scroll #test to 500px on the X- and Y-axis (1500ms duration) and output string to the console log when done
 let scrollElement = document.getElementById("test");
 scrollTo([500, 500], 1500, scrollElement).then(() => {
 window.console.log("Scroll done");
 });


 NOTES:
 ======

 Promise is not supported in IE <= 11 and Safari <= 8.
 Make sure Promise is supplied by the polyfill (version default-3.4 and up).

 */


import getElementPosition from './getElementPosition';


const defaultDuration = 1000;


/**
 * Perform the scroll action
 *
 * @param {Array} position - An array [x, y] of where to scroll to
 * @param {HTMLElement} element - Element to scroll inside
 * @param {Boolean} elementIsWindow - Is the element window
 */
function doScroll(position, element, elementIsWindow) {
    if (elementIsWindow) {
        element.scrollTo(position[0], position[1]);
    } else {
        element.scrollLeft = position[0];
        element.scrollTop = position[1];
    }
}


/**
 * Smooth scroll effect
 *
 * @param {Number|Array|HTMLElement|String} target - Where to scroll to in pixels on the Y-axis or as an array [x, y]
 *        This can also be an HTML Element or a string, which will search for an element ID
 * @param {Number|Boolean|Null} [duration] - Duration of scroll effect in ms (defaults to 1000)
 * @param {Array} offset - Offset value in pixels for X and Y (defaults to [0, 0])
 * @param {HTMLElement|String} [targetElement=window] - Element to scroll inside (defaults to window)
 * @returns {Promise}
 */
export default function scrollTo(target, duration, offset = [0, 0], targetElement = window) {
    // Make sure Promise is supported
    if (typeof Promise !== "function") {
        throw "scrollTo needs support for Promise - Please read the notes in js/utils/scrollTo.js";
    }

    // Set duration to default if it isn't defined as a number
    if (typeof duration !== "number") {
        duration = defaultDuration;
    }

    // If target element is a string, find the element by its ID
    if (typeof targetElement === "string") {
        targetElement = document.getElementById(targetElement);
    }

    // Throw error if target element wasn't found
    if (!targetElement) {
        throw "getElementPosition did not find an element.";
    }

    const elementIsWindow = (targetElement === window);
    let scrollPos = [];
    let scrollCount = 0;
    let oldTimestamp = Date.now();

    // Get current scroll position
    if (elementIsWindow) {
        if (targetElement.scrollX !== undefined && targetElement.scrollY !== undefined) {
            scrollPos = [
                targetElement.scrollX,
                targetElement.scrollY
            ];
        } else {
            scrollPos = [
                window.pageXOffset || document.documentElement.scrollLeft,
                window.pageYOffset || document.documentElement.scrollTop
            ];
        }
    } else {
        scrollPos = [
            targetElement.scrollLeft,
            targetElement.scrollTop
        ];
    }

    // If target is a number and not an array, make an array for scrolling on the Y-axis
    if (typeof target === "number") {
        target = [
            scrollPos[0],
            target
        ];
    }

    // If target is not an array by now we'll try to find the position of an element
    if (!Array.isArray(target)) {
        const elementPosition = getElementPosition(target, targetElement);

        target = [
            elementPosition.left,
            elementPosition.top
        ];
    }

    // If no X or Y position is stated, keep the current position
    if (typeof target[0] !== "number") {
        target[0] = scrollPos[0];
    }
    if (typeof target[1] !== "number") {
        target[1] = scrollPos[1];
    }

    // Subtract offset from target value
    target[0] += offset[0];
    target[1] += offset[1];

    // Calculate
    const cosParameters = [
        (scrollPos[0] - target[0]) / 2,
        (scrollPos[1] - target[1]) / 2
    ];

    return new Promise((resolve) => {
        // If duration is set to 0, just jump to the stated target/position
        if (duration <= 0) {
            doScroll(target, targetElement, elementIsWindow);
            resolve();
            return;
        }

        function step() {
            const newTimestamp = Date.now();
            const timeDifference = newTimestamp - oldTimestamp;
            let moveStep;
            let scrollDone = false;

            // Pi is used to make easing
            scrollCount += Math.PI / (duration / timeDifference);

            // As soon as we cross over Pi, we're about where we need to be
            if (scrollCount >= Math.PI) {
                moveStep = target;
                scrollDone = true;
            } else {
                // Calculate and set scroll position
                moveStep = [
                    Math.round(target[0] + cosParameters[0] + cosParameters[0] * Math.cos(scrollCount)),
                    Math.round(target[1] + cosParameters[1] + cosParameters[1] * Math.cos(scrollCount))
                ];
            }

            // Perform scroll action
            doScroll(moveStep, targetElement, elementIsWindow);

            if (scrollDone) {
                resolve();
            } else {
                oldTimestamp = newTimestamp;
                window.requestAnimationFrame(step);
            }
        }

        window.requestAnimationFrame(step);
    });
}